import Observability, { LDObserve } from '@launchdarkly/observability';

import { CILogger } from 'common/log/log.provider';

/**
 * `@launchdarkly/session-replay`'s `RecordOptions` has no network/console capture controls — this
 * separate, complementary plugin supplies them. `requestResponseSanitizer` strips request/response
 * bodies AND headers before they ever leave the browser: GraphQL/REST payloads in this app can
 * carry PHI (patient activity answers) with no reliable safe/unsafe key split to allow- or
 * deny-list instead, and headers can carry Cognito/Okta auth tokens or cookies.
 * `manualStart` records nothing until asked, matching the session replay plugin's lifecycle.
 *
 * `productAnalytics` (clicks/pageViews/trackEvents, all default `true`) is a separate pipeline
 * from `networkRecording` and from session replay's DOM masking, with no sanitizer hook of its
 * own — e.g. `clicks` records the clicked element's text content (a survey answer button's label)
 * as an OTel span attribute regardless of body/DOM redaction. This app has no use for it (Pendo
 * already owns product analytics, and nothing calls `LDObserve.track()`), so it stays off entirely.
 */
export const createObservabilityPlugin = (): Observability =>
    new Observability({
        manualStart: true,
        disableConsoleRecording: true,
        productAnalytics: false,
        networkRecording: {
            enabled: true,
            recordHeadersAndBody: true,
            requestResponseSanitizer: (pair) => {
                if (pair.request) {
                    pair.request.body = '[REDACTED]';
                    pair.request.headers = {};
                }
                if (pair.response) {
                    pair.response.body = '[REDACTED]';
                    pair.response.headers = {};
                }

                return pair;
            },
        },
    });

export const startNetworkObservability = async (): Promise<void> => {
    try {
        await LDObserve.start();
    } catch (error) {
        CILogger.getInstance().error('[LaunchDarkly] network observability failed to start', error);
    }
};

export const stopNetworkObservability = async (): Promise<void> => {
    try {
        await LDObserve.stop();
    } catch (error) {
        CILogger.getInstance().error('[LaunchDarkly] network observability failed to stop', error);
    }
};
